// Packages
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Button,
  Input,
  InputGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import axios from "axios";
import {
  BsPencilFill,
  BsTrashFill,
  BsArrowRepeat,
  BsSearch,
  BsFillPatchCheckFill,
  BsFillPatchExclamationFill,
  BsFillPersonVcardFill,
} from "react-icons/bs";
// Utils
import {
  catchHandler,
  getRequestHeaders,
  processUserName,
  userRoles,
  userRoleToLabel,
} from "../utils";
// Components
import Pagination from "../components/Pagination";
import ConfirmationModal from "../components/ConfirmationModal";
// Styling
import "./UsersManagement.css";
import { statusLabels } from "../utils/constants";

const UsersManagement = React.memo((props) => {
  const { state: locationState = {} } = useLocation();
  const { pageNumber = 1 } = locationState;
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [totalCount, setTotalCount] = useState(0);
  const history = useHistory();
  const catchHandlerBinded = catchHandler.bind(null, history);
  const [searchValue, setSearchValue] = useState("");
  const [searchTriggerValue, setSearchTriggerValue] = useState("");
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [resetPasswordModal, setResetPasswordModal] = useState({ isOpen: false, email: null });
  const [blockModal, setBlockModal] = useState({ isOpen: false, item: null, action: null });

  const getData = useCallback(
    (currentPage, searchTriggerValue, skipLoading = false) => {
      if (!skipLoading) {
        setLoading(true);
      }

      axios
        .get(
          `/api/users/list-users`,
          getRequestHeaders(
            Object.assign(
              {
                page: currentPage,
              },
              searchTriggerValue && {
                search: searchTriggerValue,
              }
            )
          )
        )
        .then((res) => {
          const { data = {} } = res;
          const { items = [], totalCount = 0 } = data;
          setUsers(items);
          setTotalCount(totalCount);
        })
        .catch(catchHandlerBinded)
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    getData(currentPage, searchTriggerValue);
  }, [currentPage, searchTriggerValue]);

  const onResetClick = useCallback((email) => {
    setResetPasswordModal({ isOpen: true, email });
  }, []);

  const toggleResetPasswordModal = useCallback(() => {
    setResetPasswordModal({ isOpen: false, email: null });
  }, []);

  const confirmResetPassword = useCallback(() => {
    const { email } = resetPasswordModal;
    if (!email) return;

    setLoadingActions(true);

    axios
      .post("/api/auth/reset-password", { email })
      .then((res) => {
        const { email } = res.data;
        toast.success(
          `Un email cu un link de resetare parola a fost trimis catre adresa [${email}].`
        );
        setResetPasswordModal({ isOpen: false, email: null });
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setLoadingActions(false);
      });
  }, [resetPasswordModal]);

  const onEditClick = useCallback(
    (id) => {
      history.push(`${history.location.pathname}/edit/${id}`);
    },
    [history]
  );

  const onSubscriptionsClick = useCallback(
    (id) => {
      history.push(`${history.location.pathname}/subscriptions/${id}`);
    },
    [history]
  );

  const onUnblockClick = useCallback((item) => {
    setBlockModal({ isOpen: true, item, action: 'unblock' });
  }, []);

  const onBlockClick = useCallback((item) => {
    setBlockModal({ isOpen: true, item, action: 'block' });
  }, []);

  const toggleBlockModal = useCallback(() => {
    setBlockModal({ isOpen: false, item: null, action: null });
  }, []);

  const confirmBlockAction = useCallback(() => {
    const { item, action } = blockModal;
    if (!item || !action) return;

    setLoadingActions(true);

    const endpoint = action === 'block'
      ? `/api/users/block-user/${item._id}`
      : `/api/users/unblock-user/${item._id}`;
    const successMessage = action === 'block'
      ? (email) => `Utilizatorul [${email}] a fost dezactivat cu succes`
      : (email) => `Utilizatorul [${email}] a fost activat cu succes`;

    axios
      .patch(endpoint, {}, getRequestHeaders())
      .then((res) => {
        const { data = {} } = res;
        const { email = "" } = data;
        toast.success(successMessage(email));
        getData(currentPage, searchTriggerValue, true);
        setBlockModal({ isOpen: false, item: null, action: null });
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setLoadingActions(false);
      });
  }, [blockModal, currentPage, searchTriggerValue]);

  const onRemoveClick = useCallback((item) => {
    setDeleteModal({ isOpen: true, item });
  }, []);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false, item: null });
  }, []);

  const confirmDelete = useCallback(() => {
    const { item } = deleteModal;
    if (!item) return;

    setLoadingActions(true);

    axios
      .delete(`/api/users/remove-user/${item._id}`, {}, getRequestHeaders())
      .then((res) => {
        const { data = {} } = res;
        const { email = "" } = data;
        toast.success(`Utilizatorul [${email}] a fost sters cu succes`);
        getData(currentPage, searchTriggerValue);
        setDeleteModal({ isOpen: false, item: null });
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setLoadingActions(false);
      });
  }, [deleteModal, currentPage, searchTriggerValue]);

  const getRows = useCallback(
    () =>
      users.length ? (
        users.map(
          ({ _id, email, metadata, role, blocked, activatedUser }, id) => (
            <tr key={`users-row-${id}`}>
              <td>{processUserName({ metadata }, true)}</td>
              <td>{email}</td>
              <td>{userRoleToLabel[role]}</td>
              <td>{!activatedUser || blocked ? statusLabels.INACTIVE : statusLabels.ACTIVE}</td>
              <td className="text-center">
                <Button
                  disabled={loadingActions}
                  color="info"
                  size="sm"
                  onClick={onResetClick.bind(null, email)}
                >
                  <BsArrowRepeat />
                </Button>
              </td>
              <td className="text-center">
                <Button
                  disabled={loadingActions}
                  color="warning"
                  size="sm"
                  onClick={onEditClick.bind(null, _id)}
                >
                  <BsPencilFill />
                </Button>
              </td>
              <td className="text-center">
                {role === userRoles.LEGITIMAT ? (
                  <Button
                    disabled={loadingActions}
                    color="success"
                    size="sm"
                    onClick={onSubscriptionsClick.bind(null, _id)}
                  >
                    <BsFillPersonVcardFill />
                  </Button>
                ) : null}
              </td>
              <td className="text-center">
                {role === userRoles.LEGITIMAT ? (
                  <Button
                    disabled={loadingActions}
                    color="secondary"
                    size="sm"
                    tag={Link}
                    to={`/users/${_id}/documents`}
                  >
                    Dosar
                  </Button>
                ) : null}
              </td>
              <td className="text-center">
                {activatedUser ? (
                  blocked ? (
                    <Button
                      color="success"
                      size="sm"
                      onClick={onUnblockClick.bind(null, { _id, email })}
                      disabled={loadingActions}
                    >
                      <BsFillPatchCheckFill />
                    </Button>
                  ) : (
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={onBlockClick.bind(null, { _id, email })}
                      disabled={loadingActions}
                    >
                      <BsFillPatchExclamationFill />
                    </Button>
                  )
                ) : null}
              </td>
              <td className="text-center">
                <Button
                  disabled={loadingActions}
                  color="danger"
                  size="sm"
                  onClick={onRemoveClick.bind(null, { _id, email, metadata })}
                >
                  <BsTrashFill />
                </Button>
              </td>
            </tr>
          )
        )
      ) : (
        <tr>
          <td colSpan={10} className="text-center">
            <span>Fara rezultat</span>
          </td>
        </tr>
      ),
    [
      onResetClick,
      onEditClick,
      onSubscriptionsClick,
      onBlockClick,
      onUnblockClick,
      onRemoveClick,
      users,
      loadingActions,
    ]
  );

  const onSearchChange = useCallback((event) => {
    const { target = {} } = event;
    const { value } = target;
    setSearchValue(() => value);
  }, []);

  const handleSearch = useCallback(
    (event) => {
      const { type, key } = event;

      if (type === "click" || (type === "keyup" && key === "Enter")) {
        setSearchTriggerValue(searchValue);
        setCurrentPage(1);
      }
    },
    [searchValue]
  );

  return (
    <Container className="mt-5 mb-5" style={{ maxWidth: "1400px" }}>
      <Row className="mt-5">
        <Col className="d-flex justify-content-between align-items-center">
          <span className="">
            <h3>Administrare Utilizatori ({totalCount})</h3>
          </span>
        </Col>
      </Row>
      <Row className="mt-1 justify-content-end">
        <Col className="col-3 d-inline-flex">
          <InputGroup>
            <Input
              type="text"
              placeholder="Search..."
              onChange={onSearchChange}
              onKeyUp={handleSearch}
              value={searchValue}
            />
            <Button
              disabled={loadingActions || loading}
              color="secondary"
              size="sm"
              onClick={handleSearch}
            >
              <BsSearch />
            </Button>
          </InputGroup>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col>
          <Table hover>
            <thead>
              <tr>
                <th>Nume</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Stare</th>
                <th className="text-center">Resetare parola</th>
                <th className="text-center">Modificare</th>
                <th className="text-center">Inrolari</th>
                <th className="text-center">Dosar</th>
                <th className="text-center">Blocare/Activare</th>
                <th className="text-center">Stergere</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center">
                    <Spinner
                      color="dark"
                      style={{ width: "3rem", height: "3rem" }}
                    />
                  </td>
                </tr>
              ) : (
                getRows()
              )}
            </tbody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            onSetPage={setCurrentPage}
          />
        </Col>
      </Row>
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        toggle={toggleDeleteModal}
        onConfirm={confirmDelete}
        title="Confirmare stergere"
        message={`Sunteti sigur ca doriti sa stergeti utilizatorul "${deleteModal.item?.email || ''}"?`}
        loading={loadingActions}
      />
      <ConfirmationModal
        isOpen={resetPasswordModal.isOpen}
        toggle={toggleResetPasswordModal}
        onConfirm={confirmResetPassword}
        title="Confirmare resetare parola"
        message={`Sunteti sigur ca doriti sa trimiteti un email de resetare parola catre "${resetPasswordModal.email || ''}"?`}
        confirmText="Da, trimite"
        confirmColor="info"
        loading={loadingActions}
      />
      <ConfirmationModal
        isOpen={blockModal.isOpen}
        toggle={toggleBlockModal}
        onConfirm={confirmBlockAction}
        title={blockModal.action === 'block' ? "Confirmare dezactivare" : "Confirmare activare"}
        message={blockModal.action === 'block'
          ? `Sunteti sigur ca doriti sa dezactivati utilizatorul "${blockModal.item?.email || ''}"?`
          : `Sunteti sigur ca doriti sa activati utilizatorul "${blockModal.item?.email || ''}"?`}
        confirmText={blockModal.action === 'block' ? "Da, dezactiveaza" : "Da, activeaza"}
        confirmColor={blockModal.action === 'block' ? "warning" : "success"}
        loading={loadingActions}
      />
    </Container>
  );
});

export default UsersManagement;
